import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from "react-redux";
import { change, submit as submitForm } from 'redux-form';
import { debounce } from '../../../helpers/debounce';
import { getAutocompleteResults, getAllLocations } from '../../../actions/autocomplete';
import { setPersonalizedValues } from '../../../actions/personalized';
import messages from '../../../locale/messages';

class PlaceGeoSuggest extends Component {

	static propTypes = {
		label: PropTypes.string,
		className: PropTypes.string,
		containerClass: PropTypes.string,
		containerClassName: PropTypes.string,
		initialValue: PropTypes.any,
		googleMaps: PropTypes.object,
		autoSearch: PropTypes.bool, // If true, automatically navigate to search when location is selected
		onSuggestSelect: PropTypes.func, // Callback when a suggestion is selected
		hideSeeAll: PropTypes.bool, // If true, hide "see all listings" option
	};


	constructor(props) {
		super(props);
		this.state = {
			value: '',
			suggestion: [],
			cachedSuggestions: [],
			allLocations: [],
		};
		this.dropdownRef = createRef();
	}

	componentDidMount() {
		const { initialValue } = this.props;
		this.setState({ value: initialValue });
		this.loadAllLocations();
		const isDocument = typeof document !== undefined;
		if (isDocument) {
			document.addEventListener('mousedown', this.handleClickOutside);
			document.addEventListener('touchstart', this.handleClickOutside);
		}
	}

	loadAllLocations = async () => {
		const { dispatch } = this.props;
		if (dispatch) {
			const result = await dispatch(getAllLocations());
			if (result && result.autocompleteLocations) {
				// Transform locations to display format
				const locations = result.autocompleteLocations.map(location => ({
					type: 'location',
					id: `location_${location}`,
					name: location,
					displayText: location,
					icon: '📍'
				}));

				this.setState({ allLocations: locations });
			}
		}
	}

	componentWillUnmount() {
		const isDocument = typeof document !== undefined;
		if (isDocument) {
			document.removeEventListener('mousedown', this.handleClickOutside);
			document.removeEventListener('touchstart', this.handleClickOutside);
		}
	}

	handleClickOutside = (event) => {
		const { id } = this.props;
		const inputElement = document.getElementById(id);

		// Only hide suggestions if the input is NOT focused
		// If input is focused, let the onBlur handler manage hiding suggestions
		if (this.dropdownRef?.current && !this.dropdownRef?.current?.contains(event?.target)) {
			if (inputElement && document.activeElement !== inputElement) {
				this.setState({ suggestion: [] });
				const parentSection = document.getElementById('container' + id);
				const childSection = document.getElementById('suggestItem' + id);
				if (parentSection && childSection) {
					parentSection.removeChild(childSection);
				}
			}
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		const { initialValue } = nextProps;
		// Only update if the value has actually changed and is not from URL params after navigation
		if (initialValue !== this.props.initialValue) {
			this.setState({ value: initialValue });
		}
	}

	handleAutoComplete = debounce(async (e) => {
		const { onChange, dispatch } = this.props;
		const value = e?.target?.value;
		onChange && onChange(value);
		
		console.log('handleAutoComplete called with value:', value);
		
		if (value?.length > 0) {
			// Get our custom autocomplete results (locations + listings)
			if (dispatch) {
				console.log('Calling getAutocompleteResults...');
				const result = await dispatch(getAutocompleteResults(value));
				console.log('Autocomplete result:', result);
				
				if (result && result.autocomplete && result.autocomplete.results) {
					// Transform our results to display format
					const suggestions = result.autocomplete.results.map(item => ({
						type: item.type, // 'location' or 'place'
						id: item.id,
						name: item.name,
						displayText: item.name,
						icon: item.type === 'location' ? '📍' : '🏠'
					}));
					
					console.log('Setting suggestions:', suggestions);
					this.setState({
						suggestion: suggestions,
						cachedSuggestions: suggestions  // Cache suggestions for onFocus behavior
					});
				} else {
					console.log('No results found, setting empty suggestions');
					this.setState({ 
						suggestion: [],
						cachedSuggestions: []  // Clear cache when no results
					});
				}
			}
		} else {
			// Don't clear suggestions when input is empty - this is handled in onChange
			// The empty case should show all locations, not clear suggestions
		}
	}, 100)

	// Simple location update - bypasses complex geography logic
	updateSimpleLocation = (locationName) => {
		const { dispatch } = this.props;
		if (dispatch) {
			// Only set the essential values needed for URL building
			dispatch(setPersonalizedValues({ name: 'location', value: locationName }));
			dispatch(setPersonalizedValues({ name: 'chosen', value: 1 })); // Mark as user-selected
			dispatch(setPersonalizedValues({ name: 'searchLocationStatus', value: false }));
			// Clear geography-related values to avoid conflicts
			dispatch(setPersonalizedValues({ name: 'geography', value: null }));
			dispatch(setPersonalizedValues({ name: 'lat', value: null }));
			dispatch(setPersonalizedValues({ name: 'lng', value: null }));
		}
	}

	handleSuggest = (suggestion) => {
		const { autoSearch, onSuggestSelect } = this.props;

		if (suggestion.type === 'location') {
			// Fill input with location name and let user set other filters
			// Keep allLocations in cachedSuggestions so when user clears/focuses again, we can show them
			this.setState({ value: suggestion.name, suggestion: [], cachedSuggestions: this.state.allLocations });

			// Call parent callback if provided (for form fields)
			if (onSuggestSelect) {
				onSuggestSelect(suggestion, suggestion.name);
			}

			// Use our simple location update instead of complex geography logic
			this.updateSimpleLocation(suggestion.name);

			// If autoSearch is enabled (for mobile), navigate immediately to search results
			if (autoSearch) {
				setTimeout(() => {
					this.navigateToSearchWithLocation(suggestion.name);
				}, 100);
			}

		} else if (suggestion.type === 'place') {
			// Navigate directly to listing page
			const listingId = suggestion.id.replace('place_', '');
			const titleSlug = suggestion.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
			const listingUrl = `/rooms/${titleSlug}-${listingId}`;
			window.location.href = listingUrl;
		} else if (suggestion.type === 'see_all') {
			// Navigate to search page with current params
			this.navigateToSearchWithParams();
		}
	}

	navigateToSearchWithLocation = async (locationName) => {
		// Submit search form with selected location instead of page reload
		const { change, submitForm } = this.props;

		if (change && submitForm) {
			// Set location in SearchForm
			await change('SearchForm', 'location', locationName);
			await change('SearchForm', 'currentPage', 1);
			// Submit the SearchForm to trigger search with new location
			submitForm('SearchForm');
		} else {
			// Fallback to URL navigation if form methods not available
			const state = this.props;
			let uri = '/s?';

			// Add the selected location
			if (locationName) {
				uri += `&address=${locationName}`;
			}

			// Get personalized data from parent form
			const personalized = state.personalized || {};

			// Add dates if available
			if (personalized.startDate && personalized.endDate) {
				uri += `&startdate=${personalized.startDate}&enddate=${personalized.endDate}`;
			}

			// Add guest count if available
			if (personalized.personCapacity && !isNaN(personalized.personCapacity)) {
				uri += `&guests=${personalized.personCapacity}`;
			}

			// Navigate to search page
			window.location.href = encodeURI(uri);
		}
	}

	navigateToSearchWithParams = () => {
		// Get current personalized values from Redux store
		const state = this.props;
		let uri = '/s?';

		// Get personalized data from parent form
		const personalized = state.personalized || {};

		// Add dates if available
		if (personalized.startDate && personalized.endDate) {
			uri += `&startdate=${personalized.startDate}&enddate=${personalized.endDate}`;
		}

		// Add guest count if available
		if (personalized.personCapacity && !isNaN(personalized.personCapacity)) {
			uri += `&guests=${personalized.personCapacity}`;
		}

		// Clear the input and navigate
		this.setState({ value: '', suggestion: [] });
		window.location.href = encodeURI(uri);
	}

	handleGeoSuggestFailure = (e) => {
		const { id } = this.props;
		const { formatMessage } = this.props.intl;
		const suggestionsContainer = document.getElementById('container' + id);
		suggestionsContainer.style.display = "block";
		let suggestionItem = document.createElement("div");
		suggestionItem.id = 'suggestItem' + id;
		suggestionItem.classList.add('suggestItem')
		suggestionItem.textContent = formatMessage(messages.locationWarning);
		if (e.target.value != "") {
			if (!document.getElementById('suggestItem' + id)) {
				suggestionsContainer.appendChild(suggestionItem);
			}
		} else {
			const parentSection = document.getElementById('container' + id);
			const childSection = document.getElementById('suggestItem' + id);
			if (parentSection && childSection) {
				parentSection.removeChild(childSection);
			}
		}
	}

	render() {
		const { placeholder, className, id, containerClass, hideSeeAll } = this.props;
		const { formatMessage } = this.props.intl;
		const { value, suggestion } = this.state;
		return (
			<div className={containerClass} ref={this.dropdownRef}>
				<div className='placeGeoSuggestContainer'>
					<div className='manualGeoSuggestInputContainer'>
						<input
							id={id}
							type='text'
							className={className}
							placeholder={placeholder}
							value={value}
							autoComplete='off'
							onChange={(e) => {
								this.setState({ value: e?.target?.value });
								if (e?.target?.value?.length == 0) {
									// When input is cleared, show all locations and don't call handleAutoComplete
									this.setState({
										suggestion: this.state.allLocations,
										cachedSuggestions: []  // Clear cache when input cleared
									});
								} else {
									// Only call handleAutoComplete if there's input
									this.handleAutoComplete(e);
								}
							}}
							onFocus={() => {
								// Always show allLocations on focus, regardless of input value
								// This ensures "view all places" list appears when clicking back to search
								if (this.state.allLocations?.length > 0) {
									this.setState({ suggestion: this.state.allLocations });
								}
							}}
							onBlur={() => {
								// Delay to allow click on suggestion items before hiding
								setTimeout(() => {
									this.setState({ suggestion: [] });
								}, 200);
							}}
						/>

						{
							suggestion?.length > 0 && (
								<ul className='manualGeoSuggestListWrapper'>
									{!hideSeeAll && (
										<li
											className='manualGeoSuggestList'
											onClick={() => this.handleSuggest({ type: 'see_all' })}
											onMouseEnter={(e) => e.target.classList.add('homepageGeoSuggestsListItemActive')}
											onMouseLeave={(e) => e.target.classList.remove('homepageGeoSuggestsListItemActive')}
											style={{ borderBottom: '1px solid #e0e0e0' }}
										>
											<div className='autocompleteItemContent'>
												<span className='autocompleteIcon'>🔍</span>
												<div style={{ flex: 1 }}>
													<div style={{ fontWeight: 500, color: 'var(--primary-foreground)' }}>
														{formatMessage(messages.seeAllListings)}
													</div>
													<div style={{ fontSize: '12px', color: '#767676', marginTop: '2px' }}>
														{formatMessage(messages.viewAllAvailableProperties)}
													</div>
												</div>
											</div>
										</li>
									)}
									{
										suggestion?.map((item, itemIndex) => {
											return (
												<li key={itemIndex}
													className='manualGeoSuggestList'
													onClick={() => this.handleSuggest(item)}
													onMouseEnter={(e) => e.target.classList.add('homepageGeoSuggestsListItemActive')}
													onMouseLeave={(e) => e.target.classList.remove('homepageGeoSuggestsListItemActive')}
												>
													<div className='autocompleteItemContent'>
														<span className='autocompleteIcon'>{item.icon}</span>
														<div style={{ flex: 1 }}>
															<div style={{ fontWeight: 500, color: 'var(--primary-foreground)' }}>
																{item.displayText}
															</div>
															<div style={{ fontSize: '12px', color: '#767676', marginTop: '2px' }}>
																{item.type === 'location' ? 'Location in Tunisia' : 'Property listing'}
															</div>
														</div>
													</div>
												</li>
											)
										})}
								</ul>
							)
						}
					</div>
				</div>
			</div>
		)
	}
}

const mapState = (state) => ({
	personalized: state.personalized
});

const mapDispatch = (dispatch) => ({
	dispatch,
	change: (form, field, value) => dispatch(change(form, field, value)),
	submitForm: (form) => dispatch(submitForm(form))
});

export default injectIntl(connect(mapState, mapDispatch)(PlaceGeoSuggest));
